import React from "react";

import { Icon, type IconName } from "./icons";

type Tab<T> = { value: T; label: string; icon?: IconName; count?: number };

function arrowKeys<T>(
  e: React.KeyboardEvent<HTMLElement>,
  options: { value: T }[],
  value: T,
  onChange: (v: T) => void,
  vertical = false,
) {
  const fwd = vertical ? "ArrowDown" : "ArrowRight";
  const back = vertical ? "ArrowUp" : "ArrowLeft";
  const step = e.key === fwd ? 1 : e.key === back ? -1 : 0;
  if (!step) return;
  e.preventDefault();
  const i = options.findIndex((o) => o.value === value);
  const next = options[(i + step + options.length) % options.length];
  onChange(next.value);
  const tabs = e.currentTarget.querySelectorAll<HTMLButtonElement>(
    "[role=tab], [role=radio]",
  );
  tabs[options.indexOf(next)]?.focus();
}

function menuKeys(e: React.KeyboardEvent<HTMLElement>) {
  const step = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
  if (!step) return;
  e.preventDefault();
  const items = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>("[role=menuitem]"),
  );
  const at = items.indexOf(document.activeElement as HTMLElement);
  items[(at + step + items.length) % items.length]?.focus();
}

/** Two to five peer modes of one view: grey track, white thumb. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = "md",
  toggle = false,
}: {
  value: T;
  options: Tab<T>[];
  onChange: (v: T) => void;
  label: string;
  size?: "sm" | "md";
  /** A setting (chart type, filter) rather than a switch between views. */
  toggle?: boolean;
}) {
  return (
    <div
      className={`ds-seg ${size}`}
      role={toggle ? "radiogroup" : "tablist"}
      aria-label={label}
      onKeyDown={(e) => arrowKeys(e, options, value, onChange)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role={toggle ? "radio" : "tab"}
          aria-selected={toggle ? undefined : o.value === value}
          aria-checked={toggle ? o.value === value : undefined}
          tabIndex={o.value === value ? 0 : -1}
          className={o.value === value ? "on" : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <Icon name={o.icon} size={14} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Underlined sections of one page. */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Tab<T>[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      className="ds-tabs"
      role="tablist"
      aria-label={label}
      onKeyDown={(e) => arrowKeys(e, options, value, onChange)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className={o.value === value ? "on" : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count != null && <span className="ds-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export type Layer<T extends string> = Tab<T> & { group?: string };

/** The list of sub-views on the left of a page's sheet. */
export function LayersNav<T extends string>({
  value,
  options,
  onChange,
  title = "Layers",
  menu = false,
}: {
  value: T;
  options: Layer<T>[];
  onChange: (v: T) => void;
  title?: string;
  /** A menu of links (the sidebar's hover panel): arrows move focus, Enter picks. */
  menu?: boolean;
}) {
  let group: string | undefined;
  return (
    <nav className="ds-layers" aria-label={title}>
      <div
        className="ds-layers-list"
        role={menu ? "menu" : "tablist"}
        aria-orientation="vertical"
        onKeyDown={(e) =>
          menu ? menuKeys(e) : arrowKeys(e, options, value, onChange, true)
        }
      >
        {options.map((o) => {
          const head = o.group !== group ? ((group = o.group), o.group) : null;
          return (
            <React.Fragment key={o.value}>
              {head && <div className="ds-layers-group">{head}</div>}
              <button
                type="button"
                role={menu ? "menuitem" : "tab"}
                aria-selected={menu ? undefined : o.value === value}
                aria-current={menu && o.value === value ? "page" : undefined}
                tabIndex={o.value === value ? 0 : -1}
                className={o.value === value ? "on" : undefined}
                onClick={() => onChange(o.value)}
              >
                {o.icon && <Icon name={o.icon} size={16} />}
                <span>{o.label}</span>
                {o.count != null && <span className="ds-count">{o.count}</span>}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}
